import { CirclePlay, Pause, Play, Volume2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface PlayPodcastProps {
  url: string;
}

const PlayPodcastMobile = ({ url }: PlayPodcastProps) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [volume, setVolume] = useState(1); // Full volume
  const [playbackRate, setPlaybackRate] = useState(1); // Default speed
  const [currentTime, setCurrentTime] = useState(0); // Current time in seconds
  const [duration, setDuration] = useState(0); // Total duration in seconds
  const [showSpeedDropdown, setShowSpeedDropdown] = useState(false);
  const [showVolDropdown, setShowVolDropdown] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      const currentAudioTime = audio.currentTime || 0;
      setCurrentTime(currentAudioTime);
      setDuration(audio.duration || 0);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateTime);
    audio.addEventListener('seeking', updateTime); // Add seeking event listener
    
    // Handle audio end event
    const handleEnded = () => {
      setIsPlaying(false);
    };
    
    // Handle audio play/pause events
    const handlePlay = () => {
      setIsPlaying(true);
    };
    
    const handlePause = () => {
      setIsPlaying(false);
    };
    
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateTime);
      audio.removeEventListener('seeking', updateTime);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [url]);

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(err => {
        console.error("Mobile: Error playing audio:", err);
        setIsPlaying(false);
      });
    }
  };

  const changeSpeed = (speed: number) => {
    setPlaybackRate(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
    setShowSpeedDropdown(false);
  };

  const adjustVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = Number(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const seekAudio = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const progressBar = e.currentTarget;
    const boundingRect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - boundingRect.left;
    const newTime = (clickX / progressBar.offsetWidth) * duration;
    
    audio.currentTime = newTime;
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  return (
    <div className="flex flex-col text-white items-center justify-center p-4 h-full gap-3 mx-auto">
      {/* Audio Element */}
      <audio ref={audioRef} src={url} />

      <div className="flex w-full justify-center gap-10">
        {/* speed */}
        <div className="relative">
          <div
            className="bg-background w-11 h-11 flex items-center justify-center rounded-lg cursor-pointer"
            onClick={() => setShowSpeedDropdown(!showSpeedDropdown)}
          >
            <CirclePlay color="white" size={20} />
          </div>

          {showSpeedDropdown && (
            <div className="absolute bottom-full flex flex-col mb-2 right-0 bg-background-main text-text-primary rounded-lg py-2 min-w-[24px] p-2 gap-3 shadow-lg text-center">
              {[0.5, 1, 1.5, 2].map((speed) => (
                <div
                  key={speed}
                  className={`px-1 hover:bg-gray-700 cursor-pointer ${
                    playbackRate === speed ? 'bg-background' : ''
                  }`}
                  onClick={() => changeSpeed(speed)}
                >
                  {speed}x
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-background w-11 h-11 flex items-center justify-center rounded-lg">
          {isPlaying ? (
            <Pause
              color="#ffffff"
              size={20}
              className={`cursor-pointer ${!duration ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={duration ? togglePlayback : undefined}
            />
          ) : (
            <Play
              color="#ffffff"
              size={20}
              className="cursor-pointer"
              onClick={togglePlayback}
            />
          )}
        </div>

        {/* Volume control */}
        <div className="relative">
          <div className="bg-background w-11 h-11 flex items-center justify-center rounded-lg">
            <Volume2
              color="#ffffff"
              size={20}
              onClick={() => setShowVolDropdown(!showVolDropdown)}
            />
          </div>
          {showVolDropdown && (
            <div className="absolute bottom-full flex flex-col mb-2 right-0 bg-background text-text-primary rounded-lg py-2 w-[40px] h-[120px] p-2 shadow-lg items-center">
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={adjustVolume}
                className="h-[100px] w-[4px] rounded-full bg-gray-400 accent-gray-700 appearance-none"
                style={{
                  WebkitAppearance: 'slider-vertical',
                  MozAppearance: 'scale-vertical',
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div
        className="flex-grow min-w-[60%] w-full h-[7px] bg-[#b1b1b3] rounded-xl cursor-pointer relative"
        onClick={seekAudio}
      >
        <div
          className="absolute h-full bg-[#FBFDFC] rounded-xl"
          style={{
            width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
          }}
        />
      </div>

      {/* time stamp */}
      <p className="flex flex-row gap-1 w-full justify-end pr-1">
        <span className="font-bold">{formatTime(currentTime)}</span>
        <span className="font-bold">/</span>
        <span className="font-semibold">{formatTime(duration)}</span>
      </p>
    </div>
  );
};

export default PlayPodcastMobile;
